import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const NewsletterContainer = styled(motion.section)`
  background: ${props => props.background || 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'};
  color: white;
  padding: 4rem 2rem;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  max-width: 1200px;
  margin: 3rem auto;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.pattern});
  background-size: cover;
  opacity: 0.05;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const NewsletterTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 1rem;
`;

const NewsletterDescription = styled.p`
  font-size: 1.1rem;
  max-width: 600px;
  margin-bottom: 2rem;
  line-height: 1.6;
  opacity: 0.9;
`;

const Form = styled.form`
  display: flex;
  max-width: 500px;
  width: 100%;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 0.8rem 1.2rem;
  border: none;
  border-radius: 5px 0 0 5px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 600px) {
    border-radius: 5px;
    margin-bottom: 1rem;
  }
`;

const SubmitButton = styled(motion.button)`
  background: ${props => props.buttonColor || '#0066cc'};
  color: white;
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 0 5px 5px 0;
  font-weight: 600;
  cursor: pointer;
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
  
  @media (max-width: 600px) {
    border-radius: 5px;
    width: 100%;
  }
`;

const Message = styled(motion.div)`
  margin-top: 1.5rem;
  padding: 0.8rem;
  border-radius: 5px;
  font-weight: 500;
  background: ${props => props.isError ? '#e74c3c' : '#2ecc71'};
  width: 100%;
  max-width: 500px;
`;

const NewsletterSignup = ({
  title = 'Stay Updated',
  description = 'Subscribe to our newsletter to get the latest updates on our projects, features and research developments.',
  buttonText = 'Subscribe',
  background,
  buttonColor = '#0066cc',
  pattern
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!email) {
      setStatus({ isError: true, message: 'Please enter a valid email address' });
      return;
    }
    
    // Simulate form submission
    setIsSubmitting(true);
    setTimeout(() => {
      // Normally, you'd make an API call to a newsletter service here
      console.log(`Subscribing email: ${email}`);
      setStatus({ isError: false, message: 'Thanks for subscribing!' });
      setEmail('');
      setIsSubmitting(false);
    }, 1500);
  };
  
  return (
    <NewsletterContainer
      background={background}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {pattern && <BackgroundPattern pattern={pattern} />}
      <ContentWrapper>
        <NewsletterTitle>{title}</NewsletterTitle>
        <NewsletterDescription>{description}</NewsletterDescription>
        
        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-label="Email address"
          />
          <SubmitButton
            type="submit"
            buttonColor={buttonColor}
            disabled={isSubmitting}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.2 }}
          >
            {isSubmitting ? 'Subscribing...' : buttonText}
          </SubmitButton>
        </Form>
        
        {status && (
          <Message
            isError={status.isError}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {status.message}
          </Message>
        )}
      </ContentWrapper>
    </NewsletterContainer>
  );
};

export default NewsletterSignup;
